import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  IconButton,
  Chip,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

function VATDialog({ open, onClose, vatRates = [], currentVATRate = 21, onApplyVAT }) {
  const { t } = useTranslation();
  const [selectedVAT, setSelectedVAT] = useState(currentVATRate);

  const handleApply = () => {
    if (selectedVAT !== null) {
      onApplyVAT(selectedVAT);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedVAT(currentVATRate); // Reset to current VAT
    onClose();
  };

  // If no VAT rates from DB, use default Belgian rates
  const defaultVATRates = [
    { Id: 1, VATPercentage: 0, Description: 'Exempt' },
    { Id: 2, VATPercentage: 6, Description: 'Reduced Rate' },
    { Id: 3, VATPercentage: 12, Description: 'Intermediate Rate' },
    { Id: 4, VATPercentage: 21, Description: 'Standard Rate' },
  ];

  const displayRates = vatRates.length > 0 ? vatRates : defaultVATRates;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PercentIcon />
          <Typography variant="h6" fontWeight={700}>
            {t('adjust_vat') || 'Adjust VAT Rate'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: alpha('#fff', 0.2) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel
            component="legend"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2,
              fontSize: '0.95rem',
            }}
          >
            {t('select_vat_rate') || 'Select VAT Rate'}
          </FormLabel>

          <RadioGroup
            value={selectedVAT}
            onChange={(e) => setSelectedVAT(parseFloat(e.target.value))}
          >
            {displayRates.map((rate) => (
              <Box
                key={rate.Id}
                sx={{
                  mb: 1,
                  p: 2,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor:
                    selectedVAT === rate.VATPercentage
                      ? '#2196f3'
                      : alpha('#2196f3', 0.2),
                  bgcolor:
                    selectedVAT === rate.VATPercentage
                      ? alpha('#2196f3', 0.08)
                      : 'transparent',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#2196f3',
                    bgcolor: alpha('#2196f3', 0.05),
                  },
                }}
                onClick={() => setSelectedVAT(rate.VATPercentage)}
              >
                <FormControlLabel
                  value={rate.VATPercentage}
                  control={
                    <Radio
                      sx={{
                        color: '#2196f3',
                        '&.Mui-checked': {
                          color: '#2196f3',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={700} color="text.primary">
                          {rate.VATPercentage}% VAT
                        </Typography>
                        {rate.Description && (
                          <Typography variant="caption" color="text.secondary">
                            {rate.Description}
                          </Typography>
                        )}
                      </Box>
                      {rate.VATPercentage === 21 && (
                        <Chip
                          label={t('default') || 'Default'}
                          size="small"
                          sx={{
                            bgcolor: '#2196f3',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>
            ))}
          </RadioGroup>
        </FormControl>

        {/* Current Selection Preview */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha('#2196f3', 0.08),
            border: '1px solid',
            borderColor: alpha('#2196f3', 0.3),
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t('selected_vat_rate') || 'Selected VAT Rate'}
          </Typography>
          <Typography variant="h5" fontWeight={800} color="#2196f3">
            {selectedVAT}%
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 4,
            fontWeight: 700,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            },
          }}
        >
          {t('apply') || 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default VATDialog;